import { useCallback, useMemo } from 'react';

import { Bean, TranslatableText } from '@dreryk/react-common';
import { ContentNode, dispatchSnackbarPositiveEvent, ModalConfirmButton } from '@dreryk/react-components';

import { FormsTemplateUse } from '../FormsTemplates.types';
import { useFormsTemplatesContext } from '../formsTemplatesContext';
import { formsTemplatesTranslationKeys } from '../translations';

type FormsTemplatesUseTemplateModalConfirmButtonProps<ITEM extends Bean> = {
   readonly onClick: FormsTemplateUse<ITEM>;
   readonly successMessage?: TranslatableText;
   readonly confirmationText?: ContentNode;
};

export const FormsTemplatesUseTemplateModalConfirmButton = <ITEM extends Bean>(
   props: FormsTemplatesUseTemplateModalConfirmButtonProps<ITEM>
): JSX.Element | null => {
   const { activeItem, mode } = useFormsTemplatesContext<ITEM>();

   const handleClick = useCallback(() => {
      if (mode !== 'READ_ONLY') {
         return;
      }
      props.onClick(activeItem);
      if (props.successMessage) {
         dispatchSnackbarPositiveEvent(props.successMessage);
      }
      // eslint-disable-next-line react-hooks/exhaustive-deps
   }, [activeItem, props.successMessage, props.onClick]);

   const confirmationConfig = useMemo(
      () => ({
         enabled: Boolean(props.confirmationText),
         title: formsTemplatesTranslationKeys.usingTemplate,
         content: props.confirmationText ?? '',
      }),
      [props.confirmationText]
   );

   if (mode !== 'READ_ONLY') {
      return null;
   }
   return (
      <ModalConfirmButton
         onClick={handleClick}
         label={formsTemplatesTranslationKeys.useTemplate}
         shouldConfirm={confirmationConfig}
      />
   );
};
